import React, { ComponentPropsWithoutRef, useMemo } from 'react'
import { sanitize } from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import { handleWithoutPropagating } from 'shared/utils/handle-without-propagating'
import { AudioPlayerAttachement } from 'modules/attachments/components/audio-player'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import AttachmentPreviewItem from 'modules/attachments/components/preview/preview-item'
import { TextWithMentions } from 'modules/community/components/editor/utils/deserealizeMentionToHTML'
import { PostPinnedHeader } from 'modules/community/components/pinned-post/components/post-pinned-header'
import { AttachmentTypeEnum } from 'modules/community/types/post-attachment'
import {
  PostInterface,
  SearchPostInterface,
  isSearchPostGuard,
} from 'modules/community/types/post-interface'
import { getPrimaryAttachment } from 'modules/community/utils/attachments'

const MAX_CONTENT_TRUNCATED_LENGTH = 125

export interface PostAnnounceLayoutProps<T extends PostInterface | SearchPostInterface>
  extends ComponentPropsWithoutRef<'div'> {
  headerTitle: React.ReactNode
  headerActions: React.ReactNode
  footerContent: React.ReactNode
  extraContent?: React.ReactNode
  postData: T
  isPinnedPost?: boolean
  isNeedTruncate?: boolean
}

export const PostAnnounceLayout = <T extends PostInterface | SearchPostInterface>({
  headerTitle,
  headerActions,
  footerContent,
  extraContent,
  postData,
  isPinnedPost,
  isNeedTruncate,
  ...props
}: PostAnnounceLayoutProps<T>) => {
  const { t } = useTranslation()

  const primaryAttachment = getPrimaryAttachment(postData.attachments)
  const isSearchPost = isSearchPostGuard(postData)
  const displayContent = isSearchPost
    ? postData.highlight.content || postData.content
    : postData.content

  const displayTitle = useMemo(() => {
    if (isSearchPost && postData.highlight?.title) {
      return sanitize(postData.highlight.title)
    }
    return sanitize(postData.title)
  }, [postData.title, isSearchPost && postData.highlight.title])

  return (
    <PostAttachmentsProvider attachments={postData.attachments}>
      {isPinnedPost && <PostPinnedHeader post={postData} className="relative rounded-t-lg" />}
      <div
        className={twJoin(
          'mb-8 w-full cursor-pointer border border-text-secondary/30 bg-background-secondary px-5 py-[25px] font-avertaPE shadow-[0px_4px_20px_0px_#F2F4F5] transition-shadow hover:shadow-[0px_4px_25px_0px_rgb(255,255,255)]',
          isPinnedPost ? 'rounded-b-lg' : 'rounded-lg',
        )}
        {...props}
      >
        <div className="mb-2 flex justify-between sm:mb-5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5">{headerTitle}</div>
          <div className="flex items-center" onClick={handleWithoutPropagating()}>
            {headerActions}
          </div>
        </div>

        <div className="flex flex-row items-start justify-between gap-2">
          <div className="w-full min-w-0">
            <h2
              className="community-post-title break-all pb-[15px] text-[22px]"
              dangerouslySetInnerHTML={{
                __html: displayTitle,
              }}
            />
            <TextWithMentions
              className={twMerge(
                'preview-image-wrapper cursor-pointer overflow-hidden text-ellipsis break-all text-base text-text-primary/90',
              )}
              truncateLength={isNeedTruncate ? MAX_CONTENT_TRUNCATED_LENGTH : undefined}
              text={displayContent}
              mentions={postData.mentions}
            />
            {!!postData?.attachments?.length && (
              <>
                {postData.attachments.map(
                  attachment =>
                    attachment.type === AttachmentTypeEnum.Audio && (
                      <AudioPlayerAttachement
                        attachment={attachment}
                        height="50px"
                        width="100%"
                        className="mt-3"
                        key={attachment.dataFileId}
                      />
                    ),
                )}
              </>
            )}
            {isNeedTruncate && (
              <button
                data-show-more
                className="pt-[15px] leading-none text-brand-primary hover:underline"
              >
                {t('post_announce.post_show_more')}
              </button>
            )}

            {!!primaryAttachment && primaryAttachment.type !== AttachmentTypeEnum.Audio && (
              <AttachmentPreviewItem
                className="block size-full max-w-[350px] sm:size-52 md:hidden"
                attachment={primaryAttachment}
                key={primaryAttachment.dataFileId}
              />
            )}

            {footerContent}
          </div>

          {!!primaryAttachment && primaryAttachment.type !== AttachmentTypeEnum.Audio && (
            <AttachmentPreviewItem
              className="min-w-fit max-md:hidden"
              attachment={primaryAttachment}
            />
          )}
        </div>
      </div>
      {extraContent}
    </PostAttachmentsProvider>
  )
}
